/**
 * 飞机分组面板 V2.2 - 集成 Agent 验证结果 + 紧凑视图 + 单元格求和
 *
 * 功能：
 * - 显示单架飞机的统计信息（日期范围、费用小计）
 * - 展示航班×产品矩阵
 * - 支持按机场关系筛选航班
 * - 航班类别标记（起飞/落地/无关）
 * - Rotation可视化显示
 * - Agent 验证结果叠加（行级状态 + 单元格级标记）
 * - 滑选单元格求和功能
 */

import React, { useMemo, useState, useRef, useCallback } from "react";
import { Card, Table, Tag, Popover, Row, Col, Divider, Checkbox, Tooltip } from "antd";
import {
    CalendarOutlined,
    DollarOutlined,
    InfoCircleOutlined,
    ArrowRightOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    WarningFilled,
    QuestionCircleOutlined,
    CalculatorOutlined,
} from "@ant-design/icons";
import { AirplaneOutlined } from "../../../../../components/icons/SvgIcons";
import RotationVisualizer from "../../../../flightOperation/Monitor/components/RotationVisualizer";
import FlightTimeDisplay from "../../../../../components/DateTimeDisplay";
import FlightDateDisplay from "../../../../../components/DateTimeDisplay/FlightDateDisplay";
import { useAirports } from "../../../../../hooks/useAirports";
import { resolveTime } from "../../../../../utils/resolveTime";
import { sortBySourceDisplayOrder } from "./sourceVisuals.js";
import { extractProductsMatrix } from "../../../components/AgentComparisonMatrix";
import { summarizeOriginalCurrencyCharges } from "./currencyDisplay.js";
import { buildApreFlightMonitorUrl } from "../../utils/flightMonitorDeepLink";

/**
 * 格式化金额
 */
const formatAmount = (amount, currency = "USD", showZero = false) => {
    if (amount == null || (amount === 0 && !showZero)) return "-";
    const num = Number(amount);
    return num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

/**
 * 航班机场关系类型标记
 */
const AIRPORT_RELATION_CONFIG = {
    DEPARTURE: { label: "起飞", color: "#52c41a", icon: <ArrowRightOutlined style={{ transform: "rotate(-45deg)" }} />, tooltip: "从关联机场起飞" },
    ARRIVAL: { label: "落地", color: "#1890ff", icon: <ArrowRightOutlined style={{ transform: "rotate(45deg)" }} />, tooltip: "落地到关联机场" },
    NONE: { label: "", color: "#d9d9d9", icon: null, tooltip: "与关联机场无关" },
};

/**
 * 验证状态图标组件
 * 根据航班级验证状态渲染对应的视觉标记
 */
const ValidationStatusIcon = ({ status }) => {
    if (!status) return null;

    const config = {
        EXECUTION_FAILED: { icon: <WarningFilled />, color: "#d46b08", tooltip: "验证执行失败" },
        HAS_ISSUES: { icon: <CloseCircleFilled />, color: "#ff4d4f", tooltip: "警告" },
        PASSED_WITH_WARNINGS: { icon: <WarningFilled />, color: "#faad14", tooltip: "提醒" },
        PASSED: { icon: <CheckCircleFilled />, color: "#52c41a", tooltip: "通过" },
    };

    const cfg = config[status];
    if (!cfg) return null;

    return (
        <Tooltip title={cfg.tooltip}>
            <span className="validation-status-icon" style={{ color: cfg.color }}>
                {cfg.icon}
            </span>
        </Tooltip>
    );
};

/**
 * 查找某个 charge 对应的验证结果
 * 优先用 source (invoice_number/item_id) 精确匹配
 * 退而用 product_code + amount 模糊匹配
 */
const findChargeValidation = (flightValidation, charge) => {
    if (!flightValidation?.charge_results) return null;

    // 精确匹配：source = "invoice_number/item_id"
    if (charge.invoice_number && charge.item_id) {
        const sourceKey = `${charge.invoice_number}/${charge.item_id}`;
        const exact = flightValidation.charge_results.find((cr) => cr.source === sourceKey);
        if (exact) return exact;
    }

    // 模糊匹配：product_code + amount
    return (
        flightValidation.charge_results.find(
            (cr) => cr.product_code === charge.product_code && Math.abs(cr.amount - charge.amount) < 0.01
        ) || null
    );
};

/**
 * 验证结果颜色/样式
 */
const VALIDATION_RESULT_STYLE = {
    FAIL: { color: "#ff4d4f", className: "charge-cell-fail", icon: <CloseCircleFilled style={{ fontSize: 9 }} />, briefColor: "#ff4d4f" },
    EXECUTION_FAILED: { color: "#ff4d4f", className: "charge-cell-fail", icon: <CloseCircleFilled style={{ fontSize: 9 }} />, briefColor: "#ff4d4f" },
    NO_RULE: { color: "#faad14", className: "charge-cell-no-rule", icon: <QuestionCircleOutlined style={{ fontSize: 9 }} />, briefColor: "#d48806" },
    PASS: { color: null, className: "charge-cell-pass", icon: <CheckCircleFilled style={{ fontSize: 9 }} />, briefColor: "#52c41a" },
};

const formatExecutionFailureAnchor = (failure) => {
    const evidence = failure?.execution_evidence || {};
    const executionId = failure?.execution_id || evidence.execution_id;
    const terminalType = failure?.terminal_error_type || evidence.terminal_error_type;
    if (executionId && terminalType) return `${executionId} (${terminalType})`;
    if (executionId) return `execution ${executionId}`;
    if (terminalType) return terminalType;
    return "未记录外部 execution id";
};

const PRODUCT_CELL_TOOLTIP_LEAVE_DELAY_SECONDS = 0.35;

const OriginalCurrencyTooltipContent = ({ summary }) => {
    if (!summary) return null;
    return (
        <div style={{ maxWidth: 340 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>原币折算</div>
            {summary.details.map((detail, index) => (
                <div key={`${detail.originalCurrency}-${detail.baseCurrency}-${index}`} style={{ marginBottom: index < summary.details.length - 1 ? 8 : 0 }}>
                    <div>{detail.baseLabel || "Base 金额未折算"}</div>
                    <div style={{ color: "#595959" }}>{detail.originalLabel}</div>
                    {detail.rateLabel && <div style={{ color: "#595959" }}>{detail.rateLabel}</div>}
                    {detail.formula && <div style={{ color: "#8c8c8c", fontSize: 11 }}>{detail.formula}</div>}
                    {!detail.rateLabel && detail.warnings.length > 0 && (
                        <div style={{ color: "#d48806", fontSize: 11 }}>{detail.warnings[0].message || detail.warnings[0].code}</div>
                    )}
                </div>
            ))}
            {summary.hasMultipleCurrencies && <div style={{ color: "#8c8c8c", fontSize: 11, marginTop: 6 }}>完整逐项明细见费用详情抽屉。</div>}
        </div>
    );
};

/**
 * 飞机分组面板
 */
export function AircraftGroupPanel({ group, products, currency, relatedAirport, onFlightClick, validationMap }) {
    // 获取机场数据用于时区转换
    const { airports: airportsMap } = useAirports();

    // -- 所有 Hooks 必须在 early return 之前调用，以确保调用顺序稳定 --
    const { aircraft_reg, date_range, flights, product_columns } = group || {};

    // 筛选条件状态（默认只显示起飞航班）
    const [filterOptions, setFilterOptions] = useState({
        DEPARTURE: true,
        ARRIVAL: false,
        NONE: false,
        REFERENCE: true,
    });

    // ===== 单元格滑选求和状态 =====
    const [selectedCells, setSelectedCells] = useState([]);
    const [isSelecting, setIsSelecting] = useState(false);
    const selectionStartRef = useRef(null);

    // 计算选中单元格的求和
    const selectionSum = useMemo(() => {
        if (selectedCells.length === 0) return null;
        let sum = 0;
        let count = 0;
        selectedCells.forEach(({ amount }) => {
            if (amount != null) {
                sum += amount;
                count++;
            }
        });
        return { sum, count };
    }, [selectedCells]);

    // 鼠标按下开始选择
    const handleCellMouseDown = useCallback((flightId, productCode, amount, e) => {
        e.preventDefault(); // 防止文字选中
        setIsSelecting(true);
        selectionStartRef.current = { flightId, productCode };
        setSelectedCells([{ flightId, productCode, amount }]);
    }, []);

    // 鼠标移入扩展选择
    const handleCellMouseEnter = useCallback(
        (flightId, productCode, amount) => {
            if (!isSelecting) return;
            setSelectedCells((prev) => {
                // 去重
                const exists = prev.some((c) => c.flightId === flightId && c.productCode === productCode);
                if (exists) return prev;
                return [...prev, { flightId, productCode, amount }];
            });
        },
        [isSelecting]
    );

    // 鼠标抬起结束选择
    const handleMouseUp = useCallback(() => {
        setIsSelecting(false);
    }, []);

    // 点击空白区域清除选择
    const handlePanelClick = useCallback((e) => {
        // 如果点击的不是单元格内部，清除选择
        if (!e.target.closest(".product-cell")) {
            setSelectedCells([]);
        }
    }, []);

    // 根据筛选条件过滤航班
    const filteredFlights = useMemo(() => {
        return (flights || []).filter((f) => {
            if (f.is_reference_flight && !filterOptions.REFERENCE) return false;
            const relation = f.airport_relation || "NONE";
            return filterOptions[relation];
        });
    }, [flights, filterOptions]);

    // 计算筛选后的统计数据
    const filteredStats = useMemo(() => {
        let subtotal = 0;
        let chargedCount = 0;
        const productSubtotals = {};

        filteredFlights.forEach((f) => {
            const flightTotal = f.current_batch_total_amount ?? f.total_amount ?? 0;
            subtotal += flightTotal;
            if (f.has_current_batch_charges) chargedCount++;

            Object.entries(f.charges_by_product || {}).forEach(([code, charges]) => {
                const productTotal = charges.reduce((sum, c) => sum + (c.is_current_batch_charge ? (c.amount || 0) : 0), 0);
                productSubtotals[code] = (productSubtotals[code] || 0) + productTotal;
            });
        });

        return { subtotal, chargedCount, flightCount: filteredFlights.length, productSubtotals };
    }, [filteredFlights]);

    // 处理筛选变化
    const handleFilterChange = (type, checked) => {
        setFilterOptions((prev) => ({ ...prev, [type]: checked }));
    };

    // 构建表格列
    const columns = useMemo(() => {
        const formatGroundTime = (minutes) => {
            if (minutes == null) return "-";
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            if (hours > 0) return mins > 0 ? `${hours}h${mins}m` : `${hours}h`;
            return `${mins}m`;
        };

        const baseColumns = [
            {
                title: "航班",
                dataIndex: "flight_number",
                key: "flight_number",
                width: 55,
                minWidth: 55,
                fixed: "left",
                render: (text, record) => {
                    const flightValidation = validationMap?.[record.flight_id];
                    const flightMonitorUrl = buildApreFlightMonitorUrl(record);
                    return (
                        <span style={{ fontWeight: 500, display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
                            <ValidationStatusIcon status={flightValidation?.status} />
                            <Tooltip title="在航班监控中打开">
                                <a
                                    href={flightMonitorUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(event) => event.stopPropagation()}
                                    style={{ fontWeight: 600 }}
                                >
                                    {text || "-"}
                                </a>
                            </Tooltip>
                        </span>
                    );
                },
            },
            {
                title: "航段",
                key: "route",
                width: 70,
                minWidth: 70,
                fixed: "left",
                render: (_, record) => {
                    const relation = record.airport_relation || "NONE";
                    const config = AIRPORT_RELATION_CONFIG[relation];
                    return (
                        <span className="monospaced-font" style={{ whiteSpace: "nowrap" }}>
                            <span style={{ color: relation === "DEPARTURE" ? config.color : undefined }}>{record.departure_airport}</span>
                            <span style={{ color: "#bfbfbf", margin: "0 1px" }}>-</span>
                            <span style={{ color: relation === "ARRIVAL" ? config.color : undefined }}>{record.arrival_airport}</span>
                        </span>
                    );
                },
            },
            {
                title: <Tooltip title="轮换">R</Tooltip>,
                key: "rotation",
                width: 24,
                minWidth: 24,
                fixed: "left",
                align: "center",
                render: (_, record) => (
                    <div className="compact-rotation-col">
                        <RotationVisualizer
                            flight={{ ...record, id: record.flight_id }}
                            allFlights={filteredFlights.map((f) => ({ ...f, id: f.flight_id }))}
                        />
                    </div>
                ),
            },
            {
                title: "实际日期",
                key: "actual_date",
                width: 50,
                minWidth: 50,
                fixed: "left",
                align: "center",
                render: (_, record) => {
                    const resolved = resolveTime(record, "atd");
                    const atd = resolved.value || record.std;
                    if (!atd) return <span style={{ color: "#d9d9d9" }}>-</span>;
                    return (
                        <FlightDateDisplay
                            etdOrStd={atd}
                            departureAirportCode={record.departure_airport}
                            targetFormat="D-MMM"
                        />
                    );
                },
            },
            {
                title: "ATD",
                dataIndex: "atd",
                key: "atd",
                width: 48,
                minWidth: 48,
                fixed: "left",
                align: "center",
                render: (_, record) => {
                    const resolved = resolveTime(record, "atd");
                    if (!resolved.value) return <span style={{ color: "#d9d9d9" }}>-</span>;
                    const isSettlement = resolved.source === "settlement";
                    return (
                        <span>
                            <FlightTimeDisplay
                                utcTime={resolved.value}
                                airportCode={record.departure_airport}
                                airportsMap={airportsMap}
                                format="HH:mm"
                            />
                            <Tag color={isSettlement ? "blue" : "default"} style={{ fontSize: "8px", lineHeight: "12px", height: "14px", padding: "0 2px", marginLeft: 2 }}>{isSettlement ? "结" : "运"}</Tag>
                        </span>
                    );
                },
            },
            {
                title: "ATA",
                dataIndex: "ata",
                key: "ata",
                width: 48,
                minWidth: 48,
                align: "center",
                render: (_, record) => {
                    const resolved = resolveTime(record, "ata");
                    const resolvedAtd = resolveTime(record, "atd");
                    if (!resolved.value) return <span style={{ color: "#d9d9d9" }}>-</span>;
                    const isSettlement = resolved.source === "settlement";
                    return (
                        <span>
                            <FlightTimeDisplay
                                utcTime={resolved.value}
                                airportCode={record.arrival_airport}
                                baseUtcTime={resolvedAtd.value || record.std}
                                airportsMap={airportsMap}
                                format="HH:mm"
                            />
                            <Tag color={isSettlement ? "blue" : "default"} style={{ fontSize: "8px", lineHeight: "12px", height: "14px", padding: "0 2px", marginLeft: 2 }}>{isSettlement ? "结" : "运"}</Tag>
                        </span>
                    );
                },
            },
            {
                title: <Tooltip title="过站时间">GT</Tooltip>,
                dataIndex: "actual_ground_time_minutes",
                key: "gt",
                width: 50,
                minWidth: 50,
                align: "center",
                render: (gt) => (
                    <span className="monospaced-font" style={{ color: gt ? "#8c8c8c" : "#d9d9d9" }}>
                        {formatGroundTime(gt)}
                    </span>
                ),
            },
        ];

        // 对产品列排序
        const sortedProductColumns = [...(product_columns || [])].sort((a, b) => {
            const infoA = products?.[a] || {};
            const infoB = products?.[b] || {};
            const categoryA = infoA.cost_type || infoA.category || "";
            const categoryB = infoB.cost_type || infoB.category || "";
            if (categoryA !== categoryB) return categoryA.localeCompare(categoryB);
            const nameA = infoA.name || a;
            const nameB = infoB.name || b;
            return nameA.localeCompare(nameB);
        });

        // 动态产品列
        const productCols = sortedProductColumns.map((code) => {
            const productInfo = products?.[code] || {};
            const productName = productInfo.name || code;
            const productUnit = productInfo.unit;
            const costType = productInfo.cost_type;
            const category = productInfo.category;
            const displayCategory = costType || category;
            const isNewProduct = productInfo.is_new === true;

            const titleContent = (
                <Tooltip
                    title={
                        <div>
                            <div><strong>{productName}</strong></div>
                            {displayCategory && <div>类型: {displayCategory}</div>}
                            {productUnit && <div>单位: {productUnit}</div>}
                            <div style={{ color: "#aaa", fontSize: 11 }}>编码: {code}</div>
                            {isNewProduct && <div style={{ color: "#52c41a", fontSize: 11, marginTop: 2 }}>✦ 本次 AI 新发现的产品</div>}
                        </div>
                    }
                >
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                        <div
                            style={{
                                textAlign: "center",
                                maxWidth: 90,
                                fontWeight: 500,
                                fontSize: 11,
                                lineHeight: 1.3,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                wordBreak: "break-word",
                            }}
                        >
                            {productName}
                            {isNewProduct && <sup style={{ color: "#52c41a", fontSize: 9, marginLeft: 1, fontWeight: 600 }}>NEW</sup>}
                        </div>
                        {productUnit && (
                            <div
                                style={{
                                    marginTop: 1,
                                    color: "#8c8c8c",
                                    fontSize: 10,
                                    lineHeight: 1.1,
                                    fontWeight: 500,
                                }}
                            >
                                {productUnit}
                            </div>
                        )}
                    </div>
                </Tooltip>
            );

            return {
                title: titleContent,
                key: code,
                width: 120,
                align: "right",
                className: "product-data-col",
                onHeaderCell: () => ({ className: "product-header-col" }),
                render: (_, record) => {
                    const charges = record.charges_by_product?.[code] || [];
                    if (charges.length === 0) {
                        return <span style={{ color: "#d9d9d9" }}>-</span>;
                    }
                    const sortedCharges = sortBySourceDisplayOrder(charges, (charge) => charge.source_type);
                    const total = charges.reduce((sum, c) => sum + (c.is_current_batch_charge ? (c.amount || 0) : 0), 0);
                    const referenceTotal = charges.reduce((sum, c) => sum + (c.is_reference_charge ? (c.amount || 0) : 0), 0);
                    const hasCommittedCharge = sortedCharges.some((charge) => charge.source_type === "COMMITTED_INVOICE");
                    const hasReferenceCharge = referenceTotal > 0;
                    const isReferenceOnlyCell = total <= 0 && hasReferenceCharge;
                    const hasCurrentBatchAmount = total > 0;
                    const originalCurrencySummary = summarizeOriginalCurrencyCharges(sortedCharges, currency, { includeReference: isReferenceOnlyCell });

                    // 检查该单元格的验证结果（合并阶段四 + 阶段4.5）
                    const flightValidation = validationMap?.[record.flight_id];
                    let worstResult = null; // FAIL > NO_RULE > PASS > null
                    // 按验证来源分类收集 reasoning
                    let billingReasonings = []; // 计费规则验证（阶段4.5）
                    let executionReasonings = []; // 验证执行层失败
                    let matchingReasonings = []; // 航班匹配验证（阶段四）
                    let briefs = [];

                    // 来源 1：阶段4.5 批次验证的 charge_results —— 收集所有结果（含 PASS）
                    if (flightValidation?.charge_results) {
                        charges.forEach((charge) => {
                            const cv = findChargeValidation(flightValidation, charge);
                            if (cv) {
                                // 收集 brief（无论结果类型）
                                if (cv.brief) {
                                    briefs.push(cv.brief);
                                } else if (cv.rule_code && cv.result !== "NO_RULE") {
                                    // 兼容旧数据：无 brief 时用 rule_code 简称
                                    const SHORT = {
                                        RULE_PRICE_FIXED: "F",
                                        RULE_PRICE_HOURLY: "H",
                                        RULE_PRICE_PERCENT: "P",
                                        RULE_PRICE_PER_UNIT: "U",
                                    };
                                    briefs.push(SHORT[cv.rule_code] || cv.rule_code.replace("RULE_", ""));
                                }
                                // 收集 reasoning 到计费规则分类
                                if (cv.reasoning) billingReasonings.push(cv.reasoning);
                                // 更新 worstResult
                                if (cv.result === "FAIL") {
                                    worstResult = "FAIL";
                                } else if (cv.result === "NO_RULE" && worstResult !== "FAIL") {
                                    worstResult = "NO_RULE";
                                } else if (cv.result === "PASS" && !worstResult) {
                                    worstResult = "PASS";
                                }
                            }
                        });
                    }

                    // 来源 2：阶段四交叉验证。优先读取后端聚合后的 products_matrix，
                    // 避免同一航班多张账单的独立 votes 被错误互判为缺失。
                    const crossProductsMatrix = extractProductsMatrix(flightValidation?.crossValidation);
                    const crossProductData = crossProductsMatrix?.[code];
                    const crossAgentResults = crossProductData?.agent_results || [];
                    const productHasExecutionFailure = crossAgentResults.some((result) => result.execution_failed);
                    const hasProductLevelValidation = Boolean(crossProductData && crossAgentResults.length > 0);
                    const flightLevelExecutionFailures = flightValidation?.executionFailures || flightValidation?.crossValidation?.execution_failures || [];
                    const fallbackFlightExecutionFailure = Boolean(
                        hasCurrentBatchAmount
                        && (flightValidation?.executionFailed || flightValidation?.status === "EXECUTION_FAILED")
                        && !hasProductLevelValidation
                    );
                    const hasExecutionFailure = productHasExecutionFailure || fallbackFlightExecutionFailure;
                    const hasBusinessMismatch = crossAgentResults.some((result) => !result.execution_failed && (result.match === false || result.missing));
                    if (hasExecutionFailure) {
                        if (worstResult !== "FAIL") worstResult = "EXECUTION_FAILED";
                        const failureText = productHasExecutionFailure
                            ? crossAgentResults
                                .filter((result) => result.execution_failed)
                                .map(formatExecutionFailureAnchor)
                                .join(" / ")
                            : flightLevelExecutionFailures.map(formatExecutionFailureAnchor).join(" / ");
                        executionReasonings.push(
                            `验证执行失败：${failureText || "未记录外部 execution id"}。该金额未获得可信验证结论，不等于金额校验不通过。`
                        );
                    }
                    if (hasBusinessMismatch || (crossProductData?.match === false && !hasExecutionFailure)) {
                        if (worstResult !== "FAIL") worstResult = "FAIL";
                        const stdAmt = formatAmount(crossProductData.standard, currency, true);
                        const extractedText = crossAgentResults.map((result) => {
                            if (result.execution_failed) return "执行失败（不作为金额不通过结论）";
                            if (result.missing) return "未提取";
                            return formatAmount(result.value, currency, true);
                        }).join(" / ");
                        matchingReasonings.push(`标准 ${stdAmt} vs 验证 ${extractedText || "—"}`);
                        matchingReasonings = [...new Set(matchingReasonings)];
                    }

                    // 合并所有 reasoning 用于判断是否显示 tooltip
                    const allReasonings = [...billingReasonings, ...executionReasonings, ...matchingReasonings];
                    const vstyle = worstResult ? VALIDATION_RESULT_STYLE[worstResult] : null;

                    const chargeLines = sortedCharges.map((charge, idx) => {
                        const qty = charge.quantity != null ? Number(charge.quantity) : null;
                        const unitPrice = charge.unit_price != null ? Number(charge.unit_price) : null;
                        const unit = charge.unit || productInfo.unit;

                        const cv = flightValidation?.charge_results
                            ? findChargeValidation(flightValidation, charge) : null;
                        let briefStr = null;
                        if (cv) {
                            if (cv.brief) {
                                briefStr = cv.brief;
                            } else if (cv.rule_code && cv.result !== "NO_RULE") {
                                const SHORT = {
                                    RULE_PRICE_FIXED: "F",
                                    RULE_PRICE_HOURLY: "H",
                                    RULE_PRICE_PERCENT: "P",
                                    RULE_PRICE_PER_UNIT: "U",
                                };
                                briefStr = SHORT[cv.rule_code] || cv.rule_code.replace("RULE_", "");
                            }
                        }

                        const hasDetail = qty != null || unitPrice != null;
                        const showIndicator = Boolean(briefStr && idx === 0 && vstyle?.icon);
                        if (!hasDetail && !showIndicator) return null;

                        const qtyStr = qty != null ? (Number.isInteger(qty) ? String(qty) : qty.toFixed(2)) : "-";
                        const qtyWithUnit = unit ? `${qtyStr} ${unit}` : qtyStr;
                        const priceStr = unitPrice != null ? unitPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 }) : "-";

                        return (
                            <div key={idx} className={`product-cell__charge-line ${charge.is_reference_charge ? "product-cell__charge-line--reference" : ""}`}>
                                {showIndicator && (
                                    <span className="product-cell__charge-line-indicator" style={{ color: vstyle?.briefColor || undefined }}>
                                        {vstyle?.icon}
                                    </span>
                                )}
                                {hasDetail && (
                                    <span className="product-cell__charge-line-formula">
                                        {qtyWithUnit}
                                        <span style={{ fontSize: 9, margin: "0 1px" }}>×</span>
                                        {priceStr}
                                    </span>
                                )}
                            </div>
                        );
                    }).filter(Boolean);

                    // 检查该单元格是否被选中（用于滑选求和）
                    const isSelected = selectedCells.some(
                        (c) => c.flightId === record.flight_id && c.productCode === code
                    );

                    // 合并的 briefText 仍用于 tooltip 顶部显示
                    const briefText = briefs.length > 0 ? briefs.join("; ") : null;

                    const cellContent = (
                        <div
                            className={`product-cell ${vstyle?.className || ""} ${isSelected ? "cell-selected" : ""}`}
                            data-validation-state={worstResult === "EXECUTION_FAILED" ? "execution-failed" : undefined}
                            aria-label={worstResult === "EXECUTION_FAILED" ? "验证执行失败，金额未获得可信验证结论" : undefined}
                            style={{
                                cursor: "pointer",
                                textAlign: "right",
                                userSelect: "none",
                                padding: "1px 0",
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onFlightClick?.({ ...record, focusProduct: code });
                            }}
                            onMouseDown={(e) => handleCellMouseDown(record.flight_id, code, total, e)}
                            onMouseEnter={() => handleCellMouseEnter(record.flight_id, code, total)}
                        >
                            {chargeLines.length > 0 && (
                                <div className="product-cell__charge-lines">
                                    {chargeLines}
                                </div>
                            )}

                            <div className={`product-cell__total-row ${isReferenceOnlyCell ? "product-cell__total-row--reference" : ""}`} style={{ color: (!isReferenceOnlyCell && vstyle && worstResult !== "PASS") ? vstyle.color : undefined }}>
                                {hasCommittedCharge && <span className="product-cell__committed-check" title="审核通过/已入库费用">✓</span>}
                                {(vstyle && worstResult !== "PASS") && vstyle.icon && <span style={{ flexShrink: 0 }}>{vstyle.icon}</span>}
                                {total > 0 && formatAmount(total, currency)}
                                {hasReferenceCharge && (
                                    <span className={`product-cell__reference-amount ${total > 0 ? "product-cell__reference-amount--inline" : ""}`} title="参照金额，不参与合计">
                                        {formatAmount(referenceTotal, currency)}
                                    </span>
                                )}
                            </div>
                            {originalCurrencySummary && (
                                <div className="product-cell__original-summary">
                                    {originalCurrencySummary.label}
                                </div>
                            )}
                        </div>
                    );

                    // 有验证或原币折算数据时 Tooltip 按来源分类显示
                    if (allReasonings.length > 0 || originalCurrencySummary) {
                        const tooltipBg = allReasonings.length === 0 ? "#fff"
                            : worstResult === "FAIL" ? "#fff2f0"
                            : worstResult === "NO_RULE" ? "#fffbe6"
                            : "#f6ffed";
                        return (
                            <Tooltip
                                mouseLeaveDelay={PRODUCT_CELL_TOOLTIP_LEAVE_DELAY_SECONDS}
                                title={
                                    <div style={{ maxWidth: 340 }}>
                                        {originalCurrencySummary && (
                                            <div style={{ marginBottom: allReasonings.length > 0 ? 8 : 0 }}>
                                                <OriginalCurrencyTooltipContent summary={originalCurrencySummary} />
                                            </div>
                                        )}
                                        {originalCurrencySummary && allReasonings.length > 0 && (
                                            <div style={{ borderTop: "1px solid rgba(0,0,0,0.06)", margin: "4px 0 8px" }} />
                                        )}
                                        {/* Brief 计算公式（加粗，顶部） */}
                                        {briefText && (
                                            <div style={{
                                                fontWeight: 600,
                                                marginBottom: 6,
                                                paddingBottom: 4,
                                                borderBottom: "1px solid rgba(0,0,0,0.06)",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 4,
                                            }}>
                                                {vstyle?.icon}
                                                <span>{briefText}</span>
                                            </div>
                                        )}
                                        {/* 计费规则验证区 */}
                                        {billingReasonings.length > 0 && (
                                            <div style={{ marginBottom: matchingReasonings.length > 0 ? 8 : 0 }}>
                                                <div style={{
                                                    display: "flex", alignItems: "center", gap: 4,
                                                    marginBottom: 4, fontSize: 11, fontWeight: 600, color: "#1890ff",
                                                }}>
                                                    <CalculatorOutlined style={{ fontSize: 10 }} />
                                                    计费规则
                                                </div>
                                                {billingReasonings.map((r, i) => (
                                                    <div key={`b${i}`} style={{
                                                        marginBottom: i < billingReasonings.length - 1 ? 4 : 0,
                                                        fontSize: 12, lineHeight: 1.5, paddingLeft: 14,
                                                    }}>
                                                        {r}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {/* 两区之间的分隔线 */}
                                        {billingReasonings.length > 0 && (executionReasonings.length > 0 || matchingReasonings.length > 0) && (
                                            <div style={{ borderTop: "1px solid rgba(0,0,0,0.06)", margin: "4px 0 8px" }} />
                                        )}
                                        {/* 验证执行区 */}
                                        {executionReasonings.length > 0 && (
                                            <div style={{ marginBottom: matchingReasonings.length > 0 ? 8 : 0 }}>
                                                <div style={{
                                                    display: "flex", alignItems: "center", gap: 4,
                                                    marginBottom: 4, fontSize: 11, fontWeight: 600, color: "#d46b08",
                                                }}>
                                                    <WarningFilled style={{ fontSize: 10 }} />
                                                    验证执行
                                                </div>
                                                {executionReasonings.map((r, i) => (
                                                    <div key={`e${i}`} style={{
                                                        marginBottom: i < executionReasonings.length - 1 ? 4 : 0,
                                                        fontSize: 12, lineHeight: 1.5, paddingLeft: 14,
                                                    }}>
                                                        {r}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {executionReasonings.length > 0 && matchingReasonings.length > 0 && (
                                            <div style={{ borderTop: "1px solid rgba(0,0,0,0.06)", margin: "4px 0 8px" }} />
                                        )}
                                        {/* 航班匹配验证区 */}
                                        {matchingReasonings.length > 0 && (
                                            <div>
                                                <div style={{
                                                    display: "flex", alignItems: "center", gap: 4,
                                                    marginBottom: 4, fontSize: 11, fontWeight: 600, color: "#722ed1",
                                                }}>
                                                    <AirplaneOutlined style={{ fontSize: 10 }} />
                                                    航班匹配
                                                </div>
                                                {matchingReasonings.map((r, i) => (
                                                    <div key={`m${i}`} style={{
                                                        marginBottom: i < matchingReasonings.length - 1 ? 4 : 0,
                                                        fontSize: 12, lineHeight: 1.5, paddingLeft: 14,
                                                    }}>
                                                        {r}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                }
                                color={tooltipBg}
                                overlayInnerStyle={{ color: "#434343" }}
                            >
                                {cellContent}
                            </Tooltip>
                        );
                    }

                    return cellContent;
                },
            };
        });

        // 小计列
        const totalCol = {
            title: "小计",
            dataIndex: "total_amount",
            key: "total_amount",
            width: 110,
            align: "right",
            fixed: "right",
            className: "subtotal-col",
            render: (val, record) => {
                if (!record.has_charges) {
                    return <span style={{ color: "#999" }}>-</span>;
                }
                const currentTotal = record.current_batch_total_amount ?? val ?? 0;
                const referenceTotal = record.reference_total_amount || 0;
                if (currentTotal > 0) {
                    return (
                        <div className="subtotal-cell">
                            <strong style={{ color: "#fa8c16" }}>{formatAmount(currentTotal, currency)}</strong>
                            {referenceTotal > 0 && (
                                <span className="subtotal-cell__reference" title="参照金额，不参与合计">{formatAmount(referenceTotal, currency)}</span>
                            )}
                        </div>
                    );
                }
                if (referenceTotal > 0) {
                    return <span className="subtotal-cell__reference" title="参照金额，不参与合计">{formatAmount(referenceTotal, currency)}</span>;
                }
                return <span style={{ color: "#999" }}>-</span>;
            },
        };

        return [...baseColumns, ...productCols, totalCol];
    }, [product_columns, products, currency, onFlightClick, filteredFlights, airportsMap, validationMap, selectedCells, handleCellMouseDown, handleCellMouseEnter]);

    // 排序后的产品列顺序（汇总行）
    const sortedProductColumns = useMemo(() => {
        return [...(product_columns || [])].sort((a, b) => {
            const infoA = products?.[a] || {};
            const infoB = products?.[b] || {};
            const categoryA = infoA.cost_type || infoA.category || "";
            const categoryB = infoB.cost_type || infoB.category || "";
            if (categoryA !== categoryB) return categoryA.localeCompare(categoryB);
            const nameA = infoA.name || a;
            const nameB = infoB.name || b;
            return nameA.localeCompare(nameB);
        });
    }, [product_columns, products]);

    // 表格数据
    const dataSource = useMemo(() => {
        return filteredFlights.map((f) => ({ key: f.flight_id, ...f }));
    }, [filteredFlights]);

    // 基础列数量：航班、航段、R、实际日期、ATD、ATA、GT = 7 列
    const BASE_COLUMNS_COUNT = 7;

    // 表格底部汇总行
    const summaryRow = () => (
        <Table.Summary fixed>
            <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={BASE_COLUMNS_COUNT}>
                    <span className="summary-row-label">
                        <strong>合计 ({filteredStats.chargedCount}/{filteredStats.flightCount})</strong>
                        <span className="summary-row-note">不含参照航班金额</span>
                    </span>
                </Table.Summary.Cell>
                {sortedProductColumns.map((code, idx) => (
                    <Table.Summary.Cell key={code} index={idx + BASE_COLUMNS_COUNT} align="right">
                        {(() => {
                            const productCharges = filteredFlights.flatMap((flight) => flight.charges_by_product?.[code] || []);
                            const originalCurrencySummary = summarizeOriginalCurrencyCharges(productCharges, currency);
                            const content = (
                                <div className="summary-row-amount-cell">
                                    <span style={{ color: "#1890ff" }}>{formatAmount(filteredStats.productSubtotals?.[code], currency)}</span>
                                    {originalCurrencySummary && (
                                        <span className="summary-row-original-summary">
                                            {originalCurrencySummary.hasMultipleCurrencies ? "多币种" : originalCurrencySummary.label}
                                        </span>
                                    )}
                                </div>
                            );
                            if (!originalCurrencySummary) return content;
                            return (
                                <Tooltip title={<OriginalCurrencyTooltipContent summary={originalCurrencySummary} />}>
                                    {content}
                                </Tooltip>
                            );
                        })()}
                    </Table.Summary.Cell>
                ))}
                <Table.Summary.Cell index={sortedProductColumns.length + BASE_COLUMNS_COUNT} align="right">
                    <strong style={{ color: "#fa8c16", fontSize: 14 }}>{formatAmount(filteredStats.subtotal, currency)}</strong>
                </Table.Summary.Cell>
            </Table.Summary.Row>
        </Table.Summary>
    );

    // 筛选器（只在有关联机场时显示）
    const renderFilter = () => {
        if (!relatedAirport) return null;

        const stats = {
            DEPARTURE: { count: 0, amount: 0 },
            ARRIVAL: { count: 0, amount: 0 },
            NONE: { count: 0, amount: 0 },
            REFERENCE: { count: 0, amount: 0 },
        };
        (flights || []).forEach((f) => {
            const relation = f.airport_relation || "NONE";
            stats[relation].count++;
            stats[relation].amount += f.current_batch_total_amount ?? f.total_amount ?? 0;
            if (f.is_reference_flight) {
                stats.REFERENCE.count++;
                stats.REFERENCE.amount += f.reference_total_amount || 0;
            }
        });

        const shortAmount = (val) => {
            if (!val) return "0";
            if (val >= 1000) return (val / 1000).toFixed(1) + "k";
            return val.toFixed(0);
        };

        return (
            <div style={{ marginLeft: "auto" }}>
                <span style={{ marginRight: 8, color: "#666", fontSize: 12 }}>筛选 ({relatedAirport}):</span>
                <Checkbox checked={filterOptions.DEPARTURE} onChange={(e) => handleFilterChange("DEPARTURE", e.target.checked)} style={{ marginRight: 12 }}>
                    <ArrowRightOutlined style={{ color: "#52c41a", marginRight: 2, transform: "rotate(-45deg)" }} />
                    起飞
                    <span style={{ color: "#999", fontSize: 11, marginLeft: 4 }}>
                        {stats.DEPARTURE.count}/{shortAmount(stats.DEPARTURE.amount)}
                    </span>
                </Checkbox>
                <Checkbox checked={filterOptions.ARRIVAL} onChange={(e) => handleFilterChange("ARRIVAL", e.target.checked)} style={{ marginRight: 12 }}>
                    <ArrowRightOutlined style={{ color: "#1890ff", marginRight: 2, transform: "rotate(45deg)" }} />
                    落地
                    <span style={{ color: "#999", fontSize: 11, marginLeft: 4 }}>
                        {stats.ARRIVAL.count}/{shortAmount(stats.ARRIVAL.amount)}
                    </span>
                </Checkbox>
                <Checkbox checked={filterOptions.NONE} onChange={(e) => handleFilterChange("NONE", e.target.checked)} style={{ marginRight: 12 }}>
                    其他
                    <span style={{ color: "#999", fontSize: 11, marginLeft: 4 }}>
                        {stats.NONE.count}/{shortAmount(stats.NONE.amount)}
                    </span>
                </Checkbox>
                <Checkbox checked={filterOptions.REFERENCE} onChange={(e) => handleFilterChange("REFERENCE", e.target.checked)}>
                    参照航班
                    <span style={{ color: "#999", fontSize: 11, marginLeft: 4 }}>
                        {stats.REFERENCE.count}/{shortAmount(stats.REFERENCE.amount)}
                    </span>
                </Checkbox>
            </div>
        );
    };

    // 统计详情弹出内容
    const statsContent = useMemo(() => {
        const productStats = {};
        filteredFlights.forEach((f) => {
            Object.entries(f.charges_by_product || {}).forEach(([code, charges]) => {
                const currentCharges = charges.filter((charge) => charge.is_current_batch_charge);
                if (currentCharges.length === 0) return;
                if (!productStats[code]) productStats[code] = { total: 0, count: 0 };
                const productTotal = currentCharges.reduce((sum, c) => sum + (c.amount || 0), 0);
                productStats[code].total += productTotal;
                productStats[code].count += currentCharges.length;
            });
        });

        const chargedAmounts = filteredFlights.filter((f) => f.has_current_batch_charges).map((f) => f.current_batch_total_amount ?? f.total_amount ?? 0);
        const avgAmount = chargedAmounts.length > 0 ? chargedAmounts.reduce((a, b) => a + b, 0) / chargedAmounts.length : 0;
        const maxAmount = chargedAmounts.length > 0 ? Math.max(...chargedAmounts) : 0;
        const minAmount = chargedAmounts.length > 0 ? Math.min(...chargedAmounts) : 0;

        return (
            <div style={{ minWidth: 240 }}>
                <Row gutter={[16, 8]}>
                    <Col span={12}>
                        <div style={{ marginBottom: 8 }}>
                            <span style={{ color: "#8c8c8c", fontSize: 12 }}>总航班</span>
                            <div style={{ fontSize: 14, fontWeight: 500 }}>{filteredStats.flightCount} 个</div>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={{ marginBottom: 8 }}>
                            <span style={{ color: "#8c8c8c", fontSize: 12 }}>有费用</span>
                            <div style={{ fontSize: 14, fontWeight: 500 }}>{filteredStats.chargedCount} 个</div>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={{ marginBottom: 8 }}>
                            <span style={{ color: "#8c8c8c", fontSize: 12 }}>平均每航班</span>
                            <div style={{ fontSize: 14, fontWeight: 500, color: "#1890ff" }}>{formatAmount(avgAmount, currency, true)}</div>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={{ marginBottom: 8 }}>
                            <span style={{ color: "#8c8c8c", fontSize: 12 }}>最高</span>
                            <div style={{ fontSize: 13 }}>{formatAmount(maxAmount, currency)}</div>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={{ marginBottom: 8 }}>
                            <span style={{ color: "#8c8c8c", fontSize: 12 }}>最低</span>
                            <div style={{ fontSize: 13 }}>{formatAmount(minAmount, currency)}</div>
                        </div>
                    </Col>
                </Row>

                {Object.keys(productStats).length > 0 && (
                    <>
                        <Divider style={{ margin: "12px 0 8px" }}>产品明细</Divider>
                        {Object.entries(productStats)
                            .sort((a, b) => b[1].total - a[1].total)
                            .slice(0, 5)
                            .map(([code, info]) => (
                                <div key={code} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                    <span style={{ color: "#666" }}>{products?.[code]?.name || code}</span>
                                    <span style={{ fontWeight: 500 }}>
                                        {formatAmount(info.total, currency)} ({info.count}次)
                                    </span>
                                </div>
                            ))}
                    </>
                )}
            </div>
        );
    }, [filteredFlights, filteredStats, products, currency]);

    // early return
    if (!group) return null;

    return (
        <div
            className="aircraft-group-panel"
            onMouseUp={handleMouseUp}
            onClick={handlePanelClick}
        >
            {/* 飞机统计头部 + 筛选器 */}
            <Card size="small" className="group-header" style={{ marginBottom: 12 }}>
                <Row align="middle" gutter={16}>
                    <Col>
                        <CalendarOutlined style={{ marginRight: 6, color: "#1890ff" }} />
                        <span>{date_range?.start || "-"} ~ {date_range?.end || "-"}</span>
                    </Col>
                    <Col>
                        <span style={{ color: "#666" }}>
                            {filteredStats.chargedCount} 有费用 / {filteredStats.flightCount} 航班
                        </span>
                    </Col>
                    <Col flex="auto">{renderFilter()}</Col>
                    <Col>
                        <Popover content={statsContent} title="统计详情" placement="bottomRight" trigger="hover">
                            <span style={{ cursor: "pointer" }}>
                                <DollarOutlined style={{ marginRight: 6, color: "#fa8c16" }} />
                                <strong style={{ fontSize: 16, color: "#fa8c16" }}>
                                    {currency} {formatAmount(filteredStats.subtotal, currency)}
                                </strong>
                                <InfoCircleOutlined style={{ marginLeft: 6, color: "#1890ff" }} />
                            </span>
                        </Popover>
                    </Col>
                </Row>
            </Card>

            {/* 滑选求和浮动提示 */}
            {selectionSum && selectionSum.count > 1 && (
                <div className="selection-sum-bar">
                    选中 {selectionSum.count} 个单元格　合计（不含参照）: <strong>{formatAmount(selectionSum.sum, currency, true)}</strong>
                </div>
            )}

            {/* 航班×产品矩阵表格 */}
            <Table
                columns={columns}
                dataSource={dataSource}
                pagination={false}
                size="small"
                scroll={{ x: "max-content" }}
                summary={summaryRow}
                rowClassName={(record) => {
                    const classes = [];
                    const flightValidation = validationMap?.[record.flight_id];
                    if (flightValidation?.status === "EXECUTION_FAILED") classes.push("flight-row-execution-failed");
                    if (flightValidation?.status === "HAS_ISSUES") classes.push("flight-row-has-issues");
                    if (record.is_reference_flight) classes.push("flight-row-reference");
                    const relation = record.airport_relation || "NONE";
                    if (relation === "NONE" && !record.has_charges) classes.push("flight-row-other");
                    return classes.join(" ");
                }}
                onRow={(record) => ({
                    onClick: () => onFlightClick?.(record),
                    style: { cursor: "pointer" },
                })}
            />
        </div>
    );
}

export default AircraftGroupPanel;
